import {HTMLAttributes, useCallback, useEffect, useState} from 'react';
import {t} from '../../../../common/getTranslations';
import {requestContext} from '../../../../common/requestContext';
import {twMerge} from '../../../../common/twMerge';
import useTypedDispatch from '../../../../common/useTypedDispatch';
import {ContentHomeProductListTab} from '../../../../design-system/organisms/ContentHomeProductListTab/ContentHomeProductListTab';
import {ContentHomeTabWrapper} from '../../../../design-system/organisms/ContentHomeTabWrapper/ContentHomeTabWrapper';
import {NoItem} from '../../../../design-system/organisms/NoItem/NoItem';
import {ApiErrorBoundary} from '../../../../errors/ApiErrorBoundary.class';
import {
  Maybe,
  SaleState,
  SingleListConnection,
  SortOption,
} from '../../../../graphql/generated/types';
import {LOG_CLICK_EVENT} from '../../../../logging/logEvent';
import {ReactQueryInfiniteType} from '../../../../reactQuery/CodegenReactQueryType.type';
import {ONLY_DATA_CONFIG} from '../../../../reactQuery/constant';
import {ReactQueryFetcher} from '../../../../reactQuery/ReactQueryFetcher';
import {ReactQueryInfiniteFetcher} from '../../../../reactQuery/ReactQueryInfiniteFetcher';
import {useLogin} from '../../../../user/useLogin.hook';
import {
  useContentHomeOverviewQuery,
  useContentHomeRecommendQuery,
  useInfiniteContentHomeProductListQuery,
} from '../../../../wrapper/contentWrapper';
import {EmoticonScript} from '../../../comment/presentational/EmoticonScript';
import {REFRESH_CASH_TYPE_HOME} from '../constant';
import {ContentOnlyLoggedInOrSaleCheckerHeadless} from '../headless/ContentOnlyLoggedInOrSaleCheckerHeadless';
import {useProductListSortOptDialog} from '../hook/useProductListSortOptDialog';
import {ContentHomeCommentTab} from '../presentational/ContentHomeCommentTab';
import {setCanRead} from '../state/contentHomeSlice';
import {CommentAllListContainer} from './CommentAllListContainer';
import {ContentHomeRecommendContainer} from './ContentHomeRecommendContainer';
import {ContentProductListConnectionContainer} from './ContentProductListConnectionContainer';

export interface ContentHomeTabPcContainerProps
  extends HTMLAttributes<HTMLDivElement> {
  seriesId: number;
}

export function ContentHomeTabPcContainer({
  seriesId,
  ...props
}: ContentHomeTabPcContainerProps) {
  const overview = useContentHomeOverviewQuery({seriesId}, ONLY_DATA_CONFIG)
    .data?.contentHomeOverview;
  const dispatch = useTypedDispatch();
  const {isMobile, isWebView} = requestContext.requestInfo.display;
  const {isLogin} = useLogin();
  const [isCheckedBoughtOnly, setIsCheckedBoughtOnly] = useState(false);
  const [selectedSortOption, setSelectedSortOption] = useState<
    SortOption | undefined
  >(undefined);
  // 서버에서 가져온 초기 selectedSortOption입니다. PC에선 Anchor 커서를 사용하기 위해 초기에 undefined로 sortType이 들어가야하는데, 이를 위해 사용합니다.
  const [initialSortOption, setInitialSortOption] = useState<
    SortOption | undefined
  >(undefined);
  const [sortOptList, setSortOptList] =
    useState<Maybe<Array<Maybe<SortOption>>>>(undefined);
  const [totalCount, setTotalCount] = useState<number>(0);
  const onToggleBoughtOnly = useCallback(() => {
    setIsCheckedBoughtOnly(!isCheckedBoughtOnly);
  }, [isCheckedBoughtOnly]);
  const onSelectSortOption = useCallback((sortOpt: SortOption) => {
    setSelectedSortOption(sortOpt);
  }, []);

  const [commentSelectedSortOpt, commentSetSelectedSortOpt] =
    useState<Maybe<SortOption>>();

  const {refetch: refetchInfiniteContentHomeProductListQuery} =
    useInfiniteContentHomeProductListQuery(
      {
        seriesId,
        boughtOnly: isCheckedBoughtOnly,
        sortType: selectedSortOption?.param,
      },
      {
        enabled: false,
        initialPageParam: {
          seriesId,
          boughtOnly: isCheckedBoughtOnly,
          sortType: selectedSortOption?.param,
        },
        getNextPageParam: () => undefined,
      }
    );

  useEffect(() => {
    const refetch = () => {
      refetchInfiniteContentHomeProductListQuery();
    };

    window.addEventListener(REFRESH_CASH_TYPE_HOME, refetch);
    return () => {
      window.removeEventListener(REFRESH_CASH_TYPE_HOME, refetch);
    };
  }, [refetchInfiniteContentHomeProductListQuery]);

  const {showDialog, productListSortOptDialog} = useProductListSortOptDialog({
    isMobile,
    sortOptList,
    selectedSortOpt: selectedSortOption ?? initialSortOption,
    onSelectSortOption,
    isProductList: true,
  });
  return (
    <ContentHomeTabWrapper
      className="flex flex-col"
      isMobile={false}
      {...props}
    >
      <ApiErrorBoundary className="bg-theme-bg-surface-alt">
        <ContentHomeProductListTab
          mode={'totalList'}
          boughtOnly={isCheckedBoughtOnly}
          onClickBoughtOnly={onToggleBoughtOnly}
          totalCount={totalCount}
          selectedSortOption={selectedSortOption ?? initialSortOption}
          sortOptionList={sortOptList as SortOption[] | undefined}
          onClickSortOption={showDialog}
          hideBoughtOnlyOption={
            overview?.content?.saleState !== SaleState.Ing || !isLogin
          }
          isMobile={isMobile}
          isWebView={isWebView}
        >
          <ContentOnlyLoggedInOrSaleCheckerHeadless seriesId={seriesId}>
            <ReactQueryInfiniteFetcher
              reactQuery={useInfiniteContentHomeProductListQuery}
              variables={{
                seriesId,
                boughtOnly: isCheckedBoughtOnly,
                sortType: selectedSortOption?.param,
              }}
              loadingWrapperClassName="bg-theme-bg-surface-alt"
              onSuccess={data => {
                if (
                  (data.pages[0].contentHomeProductList?.totalCount || 0) > 0
                ) {
                  dispatch(setCanRead({seriesId, canRead: true}));
                }
                setInitialSortOption(
                  data.pages[0].contentHomeProductList?.selectedSortOption
                );
              }}
              options={{
                initialPageParam: {
                  seriesId,
                  boughtOnly: isCheckedBoughtOnly,
                  sortType: selectedSortOption?.param,
                },
                getPreviousPageParam: firstPage => {
                  return firstPage.contentHomeProductList?.pageInfo
                    .hasPreviousPage
                    ? {
                        seriesId,
                        boughtOnly: isCheckedBoughtOnly,
                        sortType:
                          firstPage.contentHomeProductList?.selectedSortOption
                            .param,
                        before:
                          firstPage.contentHomeProductList?.pageInfo
                            .startCursor,
                      }
                    : undefined;
                },
                getNextPageParam: lastPage => {
                  return lastPage.contentHomeProductList?.pageInfo.hasNextPage
                    ? {
                        seriesId,
                        boughtOnly: isCheckedBoughtOnly,
                        sortType:
                          lastPage.contentHomeProductList?.selectedSortOption
                            .param,
                        after:
                          lastPage.contentHomeProductList?.pageInfo.endCursor,
                      }
                    : undefined;
                },
              }}
              type={ReactQueryInfiniteType.ArrowBoth}
              onStartFetchPrevPage={e =>
                LOG_CLICK_EVENT('위_회차_가져오기_클릭', e)
              }
              onStartFetchNextPage={e =>
                LOG_CLICK_EVENT('아래_회차_가져오기_클릭', e)
              }
            >
              {data => {
                const connectionList: SingleListConnection[] = [];
                const productInfoData = data.pages[0].contentHomeProductList;
                const hasNextPage =
                  data.pages[data.pages.length - 1]?.contentHomeProductList
                    ?.pageInfo.hasNextPage;
                setSortOptList(productInfoData?.sortOptionList);
                setTotalCount(productInfoData?.totalCount ?? 0);
                data.pages.map(page => {
                  connectionList.push({
                    edges: page.contentHomeProductList?.edges,
                    pageInfo: page.contentHomeProductList?.pageInfo,
                    selectedSortOption:
                      page.contentHomeProductList?.selectedSortOption,
                    sortOptionList: page.contentHomeProductList?.sortOptionList,
                    totalCount: page.contentHomeProductList?.totalCount,
                  } as SingleListConnection);
                });
                return (
                  /* PC에선 이 부분이 요약회차목록에 보여지는데 이는 회차가 없을 때 노출하지 않음(기획결정).
                          단 구매한 작품 선택 된 경우 필터링 개념이므로 항상 보여준다.
                          비로그인인데 판매중이 아니라면 무조건 보여줄 내용이 없으므로 보여주지 않는다. */
                  <>
                    {!!productInfoData?.totalCount && (
                      <ContentProductListConnectionContainer
                        seriesId={seriesId}
                        sortType={selectedSortOption?.param}
                        connectionList={connectionList}
                        className={!hasNextPage ? 'pb-16pxr' : undefined}
                      />
                    )}
                    {!productInfoData?.totalCount && isCheckedBoughtOnly && (
                      <NoItem isMobile={false}>
                        {t('contenthome_ep_tab_purchased_ep_blank')}
                      </NoItem>
                    )}
                  </>
                );
              }}
            </ReactQueryInfiniteFetcher>
          </ContentOnlyLoggedInOrSaleCheckerHeadless>
        </ContentHomeProductListTab>
      </ApiErrorBoundary>
      {productListSortOptDialog}
      <ReactQueryFetcher
        variables={{seriesId}}
        reactQuery={useContentHomeRecommendQuery}
        options={{throwOnError: false}}
        ignoreLoading={true}
        ignoreFetching={true}
      >
        {data =>
          data.contentHomeRecommend && (
            <ContentHomeRecommendContainer
              seriesId={seriesId}
              recommendData={data.contentHomeRecommend}
              className={twMerge(!isMobile && 'pt-4pxr')}
            />
          )
        }
      </ReactQueryFetcher>
      <div className="flex flex-col flex-1 overflow-hidden mt-4pxr min-h-200pxr rounded-12pxr bg-theme-bg-surface-alt">
        <ApiErrorBoundary className="bg-theme-bg-surface-alt">
          <ContentHomeCommentTab
            seriesId={seriesId}
            selectedSortOpt={commentSelectedSortOpt}
            setSelectedSortOpt={commentSetSelectedSortOpt}
            className="flex flex-col flex-1 pt-16pxr"
          >
            <EmoticonScript />
            <CommentAllListContainer
              seriesId={seriesId}
              selectedSortOpt={commentSelectedSortOpt}
              isContentHome={true}
            />
          </ContentHomeCommentTab>
        </ApiErrorBoundary>
      </div>
    </ContentHomeTabWrapper>
  );
}
